class BottomSheet extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);

    this.moved = false;
  }

  connectedCallback() {
    // Query elements after DOM is ready
    this.overlay = this.querySelector('.bottom-sheet__overlay');
    this.container = this.querySelector('.bottom-sheet__container');
    this.closeButton = this.querySelector('.bottom-sheet__close-button');

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.close);
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.close);
    }

    // Initial state setup
    this.setAttribute('aria-hidden', 'true');
    this.classList.add('hidden');

    // Move to body only once
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }
  }

  disconnectedCallback() {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.close);
    }

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.close);
    }

    document.removeEventListener('keyup', this.onKeyUp);
  }

  open() {
    if (this.isOpen) return;

    // Store the element that triggered the open for focus restoration
    this.triggerElement = document.activeElement;

    // Lock scroll on body
    document.body.classList.add('overflow-hidden');
    document.body.style.position = 'relative';
    document.body.style.overflowX = 'hidden';

    // Show the bottom sheet
    this.setAttribute('aria-hidden', 'false');
    this.classList.remove('hidden');

    // Force a reflow to enable transitions
    void this.offsetWidth;

    // Add active class to trigger transitions
    this.classList.add('active');

    // Set overlay properties immediately to match the transition
    this.overlay.style.backdropFilter = `blur(var(--drawer-shadow-blur-radius))`;
    this.overlay.style.backgroundColor = `rgba(var(--color-foreground), 0.6)`;
    this.overlay.style.visibility = 'visible';
    this.overlay.style.opacity = '1';

    // Add ESC key listener
    document.addEventListener('keyup', this.onKeyUp);

    this.isOpen = true;

    // Dispatch open event
    this.dispatchEvent(new CustomEvent('bottomsheet:opened'));

    // Focus trap - keep focus inside the modal when user tabs
    this.#trapFocus();
  }

  #trapFocus() {
    const focusableElements = this.container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Document-level listener to catch Tab from outside the modal
    this._focusTrapListener = (e) => {
      if (e.key !== 'Tab') return;

      const focusIsInside = this.container.contains(document.activeElement);

      if (!focusIsInside) {
        // Focus is outside the modal - bring it in
        e.preventDefault();
        firstFocusable.focus();
        return;
      }

      if (e.shiftKey) {
        // Shift + Tab: if on first element, go to last
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    document.addEventListener('keydown', this._focusTrapListener);

    // Move focus into the bottom sheet so keyboard users land inside it
    const initialFocus = firstFocusable || this.closeButton;
    if (initialFocus) {
      initialFocus.focus();
    }
  }

  close() {
    if (!this.isOpen) return;

    // Remove active class to trigger closing transition
    this.classList.remove('active');

    // Reset overlay properties with transition
    this.overlay.style.opacity = '0';

    // Wait for transition to complete
    this.container.addEventListener(
      'transitionend',
      () => {
        this.overlay.style.visibility = 'hidden';
        this.classList.add('hidden');
        this.setAttribute('aria-hidden', 'true');

        // Unlock scroll on body
        document.body.classList.remove('overflow-hidden');
        document.body.style.position = '';
        document.body.style.overflowX = '';
        // Reset transform if any
        this.container.style.transform = '';

        // Return focus to the trigger element
        if (this.triggerElement) {
          this.triggerElement.focus();
        }

        this.dispatchEvent(new CustomEvent('bottomsheet:closed'));
      },
      { once: true }
    );

    // Remove ESC key listener
    document.removeEventListener('keyup', this.onKeyUp);

    // Remove focus trap listener
    if (this._focusTrapListener) {
      document.removeEventListener('keydown', this._focusTrapListener);
    }

    this.isOpen = false;
  }

  onKeyUp(event) {
    if (event.code === 'Escape') {
      this.close();
    }
  }
}

customElements.define('bottom-sheet', BottomSheet);
